import React, { useState } from 'react';
import { AlertColor } from '@mui/material'; 
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TablePagination,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Delete as DeleteIcon,
    FileDownload
} from '@mui/icons-material';
import { DpeComplet } from '../interfaces/DpeComplet';
import { deleteDpeFile, downloadDpeExcel } from '../services/dpeService';
import Notification from './Notification';
const etiquetteStyles = {
    'A': 'bg-green-100 text-green-600',
    'B': 'bg-lime-100 text-lime-600',
    'C': 'bg-yellow-100 text-yellow-600',
    'D': 'bg-orange-100 text-orange-600',
    'E': 'bg-red-100 text-red-500',
    'F': 'bg-red-200 text-red-600',
    'G': 'bg-red-300 text-red-700'
};

interface DpeTableProps {
    dpes: DpeComplet[];
    onEditDpe: (dpe: DpeComplet) => void;
    refreshDpes: () => void;
    totalElements: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (newPage: number) => void;
    onRowsPerPageChange: (newRowsPerPage: number) => void;
    numeroDPE: string;
    logiciel: string;
    version: string;
    typeBatiment: string;
    zoneClimatique: string;
    classeAltitude: string;
}

const DpeTable: React.FC<DpeTableProps> = ({ dpes, refreshDpes, totalElements, page, rowsPerPage, onPageChange, onRowsPerPageChange, 
    numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude }) => {

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [dpeToDelete, setDpeToDelete] = useState<DpeComplet | null>(null);

    const handleDeleteClick = (dpe: DpeComplet) => {
        setDpeToDelete(dpe);
        setDeleteDialogOpen(true);
    };

    /*
    const handleExportVerificationAPE = async (numeroDPE: string) => {
        setIsExportingText(numeroDPE);
        try {
            await exportTextVerificationAPE(numeroDPE);
            //refreshProjects();
        } catch (error) {
            console.error('Erreur lors de l\'export du fichier de vérification', error);
        } finally {
            setIsExportingText(null);
        }
    };

     */

    const deleteDpe = async (dpeID: string) => {
        try {
            await deleteDpeFile(Array.of(dpeID));
        } catch (err: any) {
            console.error(err.message || 'Une erreur est survenue lors de l\'enregistrement');
        } 
    };

    const handleDeleteConfirm = async () => {
        if (dpeToDelete) {
            try {
                await deleteDpe(dpeToDelete.dpe.id);
                refreshDpes();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
        setDeleteDialogOpen(false);
        setDpeToDelete(null);
    };

    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleExcelExport = async (dpeId: string) => {
        try {
            await downloadDpeExcel(dpeId);
            setNotification({
                open: true,
                message: 'Le fichier Excel a été téléchargé avec succès',
                severity: 'success'
            });
        } catch (error) {
            setNotification({
                open: true,
                message: 'Erreur lors du téléchargement du fichier Excel',
                severity: 'error' 
            });
        }
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow className="bg-gray-50">
                            <TableCell className="font-semibold">Numéro DPE</TableCell>
                            <TableCell className="font-semibold">Logiciel</TableCell>
                            <TableCell className="font-semibold">Version XML</TableCell>
                            <TableCell className="font-semibold">Fiabilité Energie</TableCell>
                            <TableCell className="font-semibold">Fiabilité GES</TableCell>
                            <TableCell className="font-semibold">Type bâtiment</TableCell>
                            <TableCell className="font-semibold">Zone climatique</TableCell>
                            <TableCell className="font-semibold">Classe altitude</TableCell>
                            <TableCell className="font-semibold">Version</TableCell>
                            <TableCell className="font-semibold">Etiquette</TableCell>
                            <TableCell className="font-semibold">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dpes.length > 0 ? (
                            dpes.map((dpe) => (
                                <TableRow key={dpe.dpe.id} hover>
                                    <TableCell>{dpe.dpe.numeroDpe}</TableCell>
                                    <TableCell>{dpe.diagnostiqueur.versionLogiciel}</TableCell>
                                    <TableCell>{dpe.dpe.versionXML}</TableCell>
                                    <TableCell>{dpe.dpe.fiabiliteEnergie ? Number(dpe.dpe.fiabiliteEnergie.toFixed(2)) : ''}</TableCell>
                                    <TableCell>{dpe.dpe.fiabiliteGES ? Number(dpe.dpe.fiabiliteGES.toFixed(2)) : ''}</TableCell>
                                    <TableCell>{dpe.dpe.typeBatiment}</TableCell>
                                    <TableCell>{dpe.logement.zoneClimatique}</TableCell>
                                    <TableCell>{dpe.logement.classeAltitude}</TableCell>
                                    <TableCell>{dpe.dpe.versionID}</TableCell>
                                    <TableCell>
                                        <div className={`
                                            px-4 py-1 rounded-full text-center font-bold w-10
                                            ${etiquetteStyles[dpe.epConso.classeBilanDpe as keyof typeof etiquetteStyles]}
                                        `}>
                                            {dpe.epConso.classeBilanDpe}
                                        </div>

                                        <div className={`
                                            px-4 py-1 rounded-full text-center font-bold w-10
                                            ${etiquetteStyles[dpe.emissionGes.classeEmissionGes as keyof typeof etiquetteStyles]}
                                        `}>
                                            {dpe.emissionGes.classeEmissionGes}
                                        </div>

                                        <div className={`
                                            px-4 py-1 rounded-full text-center font-bold w-10
                                            ${etiquetteStyles[dpe.dpe.etiquette as keyof typeof etiquetteStyles]}
                                        `}>
                                            {dpe.dpe.etiquette}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Télécharger Résultat calcul">
                                        <IconButton
                                            onClick={() => handleExcelExport(dpe.dpe.numeroDpe)}
                                            size="small"
                                            color='success'
                                            className="text-green-600 hover:text-green-800"
                                            title="Exporter en Excel"
                                        >
                                            <FileDownload />
                                        </IconButton>
                                        </Tooltip>

                                        {/*
                                        <Tooltip title="Télécharger Vérification APE">
                                        <IconButton
                                                onClick={() => handleExportVerificationAPE(dpe.dpe.numeroDpe)}
                                                color="warning"
                                                title={'Télécharger fichier de vérification ' }
                                                disabled={isExportingText === dpe.dpe.numeroDpe}
                                            >
                                                {isExportingText === dpe.dpe.numeroDpe ? <CircularProgress size={24} /> : <FileDownload />}
                                            </IconButton>
                                        </Tooltip>
                                        */}
                                        <Tooltip title="Supprimer">
                                            <IconButton
                                                onClick={() => handleDeleteClick(dpe)}
                                                color='error'
                                                size="small"
                                                className="text-red-600"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {numeroDPE || logiciel || version || typeBatiment || zoneClimatique || classeAltitude 
                                        ? `Aucun DPE trouvé pour "${numeroDPE} ${logiciel} ${version} ${typeBatiment} ${zoneClimatique} ${classeAltitude}"`
                                        : "Aucun DPE disponible"}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={totalElements} // Utiliser le nombre réel d'éléments
                page={page}
                onPageChange={(_, newPage) => {
                    console.log('Changement de page dans TablePagination:', newPage); // Pour debug
                    onPageChange(newPage);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    const newSize = parseInt(event.target.value, 10);
                    console.log('Changement de taille dans TablePagination:', newSize); // Pour debug
                    onRowsPerPageChange(newSize);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
                labelRowsPerPage="Lignes par page"
                labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} sur ${count !== -1 ? count : 'plus de ' + to}`}
            />

            {/* Dialog de confirmation de suppression */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    Êtes-vous sûr de vouloir supprimer ce DPE ?
                    Cette action est irréversible.
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        className="text-gray-600"
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        className="text-red-600"
                        autoFocus
                    >
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>

            <Notification 
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
            />
        </>
    );
};

export default DpeTable;